import * as Sentry from '@sentry/nextjs'
import { useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import { localeToCountryCode } from '@/types/locales'

import { useLead } from '@/context/leadContext'

import { getUserType } from '@/components/shared/stripe-checkout-form/helpers'

import { getLeadId } from '@/lib/attribution/leadId'
import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { forwardMarketingPurchase } from '@/lib/checkout/forwardMarketingPurchase'
import { captureGtmServerPreviewToken } from '@/lib/checkout/gtmServerPreview'
import { extractPurchaseSessionIds, resolvePurchaseCountryCode } from '@/lib/checkout/purchaseSessionParams'
import { recordMergedAttributionPurchase, trackSessionPurchaseEvents } from '@/lib/checkout/purchaseTracking'
import { fetchSessionDetails } from '@/lib/checkout/sessionDetailsCache'
import { hasTrackedPurchase } from '@/lib/checkout/trackedPurchaseSessions'
import { firebaseAuthSource } from '@/lib/getUserAuthHeaders'
import { useAuthInfo } from '@/lib/hooks/useAuthInfo'
import { purchaseProductOf, recordPurchase } from '@/lib/sentry/paymentTiming'

/**
 * Reports a purchase as soon as a paid Stripe checkout session is seen on the URL. Every
 * post-payment URL carries the session id (see `purchaseSessionParams`), so mounting this once at
 * the app root catches whichever page the customer reaches first.
 */
export const PurchaseTracker = () => {
  const searchParams = useSearchParams()
  const router = useTrackedRouter()
  const { authenticatedUser } = useAuthInfo()
  const { leadData } = useLead()

  const guestEmail = leadData?.email ?? undefined
  const locale = typeof router.query.locale === 'string' ? router.query.locale : undefined

  useEffect(() => {
    // Before the guards: the tester lands with ?gtm_preview= long before any purchase exists.
    captureGtmServerPreviewToken(searchParams)

    if (!authenticatedUser) return

    // Every session on the URL, not only those still owed a dataLayer event: attribution merges the
    // whole basket. fetchSessionDetails caches, so re-reading one is free.
    const sessionIds = extractPurchaseSessionIds(searchParams)
    if (sessionIds.length === 0) return
    if (sessionIds.every((id) => hasTrackedPurchase(id)) && !getLeadId(searchParams)) return

    const countryCode = resolvePurchaseCountryCode(searchParams, locale ? localeToCountryCode(locale) : undefined)
    if (!countryCode) return

    const userType = getUserType(authenticatedUser)

    const fetched = sessionIds.map((sessionId) => {
      // Read before the dataLayer push below marks it, so a purchase already reported on an earlier
      // page is not sent to Sentry again; recordPurchase dedupes within this page.
      const isNewPurchase = !hasTrackedPurchase(sessionId)
      return fetchSessionDetails(firebaseAuthSource(authenticatedUser), countryCode, sessionId, guestEmail)
        .then((session) => {
          if (isNewPurchase) {
            const product = purchaseProductOf((session.line_items ?? []).map((item) => item.price?.product_type))
            recordPurchase(sessionId, session.status === 'COMPLETE' ? 'complete' : 'open', product)
          }
          // An OPEN session can still be paid; it is picked up on the next page carrying it.
          if (session.status !== 'COMPLETE') return null
          // Both dedupe internally on their own channel, so re-reading a session for attribution
          // cannot double-report either.
          trackSessionPurchaseEvents({
            session,
            countryCode,
            userType,
            userEmail: authenticatedUser.email ?? undefined,
          })
          // Server-side copy of the same purchase, gated on marketing consent. Independent of the
          // dataLayer push above: it waits for a consent answer, so it must not hold that up.
          void forwardMarketingPurchase(session, countryCode)
          return session
        })
        .catch((error: unknown) => {
          Sentry.captureException(error, {
            extra: { firebaseUID: authenticatedUser.uid, sessionId, countryCode, tracker: 'PurchaseTracker' },
          })
          return null
        })
    })

    // Attribution's copy, once the whole basket is known.
    void Promise.all(fetched).then((sessions) => {
      recordMergedAttributionPurchase(
        sessions.filter((session) => session !== null),
        getLeadId(searchParams),
      )
    })
  }, [authenticatedUser, searchParams, locale, guestEmail])

  return null
}
